import React, { useEffect, useState } from "react";
import styles from "./school-departments.module.css";
import { Tooltip, Typography } from "antd";
import CustomButton from "../../../../../../../common/custom-button/custom-button";
import { PlusOutlined } from "@ant-design/icons";
import CustomTable from "../../../../../../../common/custom-table/custom-table";
import SVGIcon from "../../../../../../../constants/SVGIcon";
import { fetchSubjectTypes } from "../../../../../../../services/subject";
import { cardColorArr } from "../../../../../../../constants/common";
import CustomTag from "../../../../../../../common/custom-tag/custom-tag";
import AddEditDepartments from "./components/add-edit-departments/add-edit-departments";
import CustomPopover from "../../../../../../../common/custom-popover/custom-popover";
import { deleteDepartment } from "../../../../../../../services/school_info";
import { refetchSchool } from "../../../../../../../utils/common";

const SchoolDepartments = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editDetails, setEditDetails] = useState(null);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    setPageNo(1);
    getSubjects();
  }, []);

  const getSubjects = async (tempPage = pageNo, tempPageSize = pageSize) => {
    try {
      setLoading(true);
      const payload = {
        page: tempPage,
        pageSize: tempPageSize,
      };
      const response = await fetchSubjectTypes(payload);
      if (response?.data) {
        const data = response?.data?.data?.departments;
        setDepartments(data);
        setTotalCount(response?.data?.data?.totalRecords);
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  const handleEditDepartment = (data) => {
    setEditDetails(data);
    setOpenDrawer(true);
  };

  const handleOnClose = () => {
    getSubjects();
    setOpenDrawer(false);
  };

  const handleDeleteDepartment = async (id) => {
    try {
      await deleteDepartment(id);
      getSubjects();
      refetchSchool();
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddDepartment = () => {
    setEditDetails(null);
    setOpenDrawer(true);
  };

  const handleChangePage = (page, pageSize) => {
    setPageNo(page);
    setPageSize(pageSize);
    getSubjects(page, pageSize);
  };

  const columns = [
    {
      title: "Department ID",
      dataIndex: "department_number",
      key: "department_number",
      render: (value) => value || "-",
    },
    {
      title: "Department Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a?.name?.localeCompare(b?.name),
      render: (value, record) => {
        const randomIndex = Math.floor(Math.random() * cardColorArr.length);
        const colorStyle = cardColorArr[randomIndex];
        const colour_code = record?.colour_code;
        return (
          <CustomTag
            text={value}
            color={colour_code ? colour_code : colorStyle?.color}
            background={colour_code ? `${colour_code}1A` : colorStyle?.background}
            borderColor={colour_code ? `${colour_code}66` : colorStyle?.border}
          />
        );
      },
    },
    {
      title: "Subjects",
      dataIndex: "subjects",
      key: "subjects",
      sorter: (a, b) => {
        const aClasses = a?.subjects?.map(c => c?.name).join(", ") || "";
        const bClasses = b?.subjects?.map(c => c?.name).join(", ") || "";
        return aClasses?.localeCompare(bClasses);
      },
      render: (value) => {
        const subjects = value?.map(item => item?.name) || [];
        if (subjects?.length === 0) return "-";
        const visibleSubjects = subjects?.slice(0, 3).join(", ");
        const extraSubjects = subjects?.length > 3 ? subjects?.slice(3) : [];

        return (
          <div className={styles["subjects-wrapper"]}>
            {visibleSubjects}
            {extraSubjects?.length > 0 && (
              <Tooltip 
                title={extraSubjects?.join(", ")}
                color="var(--primary-color)"
              >
                <span className={styles["subject-more-tooltip"]}>
                  +{extraSubjects?.length}
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <div className={styles.actionItemWrapper}>
          <SVGIcon
            name={"edit-icon"}
            width={"20"}
            height={"20"}
            stroke={"#3BB44B"}
            onClick={() => handleEditDepartment(record)}
          />
          <CustomPopover
            message="Are you sure you want to delete this department?"
            confirmText="Delete"
            cancelText="Cancel"
            onConfirm={() => handleDeleteDepartment(record?.id)}
          >
            <SVGIcon
              name={"delete"}
              width={"20"}
              height={"20"}
              stroke={"rgba(243, 17, 17, 1)"}
            />
          </CustomPopover>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className={styles.titleContainer}>
        <Typography className={styles.tabTitle}>
          Departments
        </Typography>
        <CustomButton
          title="Add New Departments"
          size="large"
          icon={<PlusOutlined />}
          onClick={handleAddDepartment}
        />
      </div>

      <div className={styles.tableContainer}>
        <CustomTable
          columns={columns}
          dataSource={departments}
          loading={loading}
          headerColor="#667085"
          headerBackgroundColor="#F9FAFB"
          total={totalCount}
          pageSize={pageSize}
          currentPage={pageNo}
          handlePageChange={handleChangePage}
        />
      </div>
      <AddEditDepartments
        open={openDrawer} 
        onClose={handleOnClose} 
        data={editDetails}
      />
    </div>
  );
};

export default SchoolDepartments;